import React, { useState } from 'react';
import { Copy, Download, Eye, EyeOff } from 'lucide-react';

const BillTemplate = ({ onUseTemplate }) => {
  const [showTemplate, setShowTemplate] = useState(false);
  const [copied, setCopied] = useState(false);

  const billTemplate = `Model Congress 2024

YOUR NAME                    YOUR COMMITTEE                    YOUR SCHOOL
Author                       Committee                          Delegation

Title of Bill: YOUR BILL TITLE

Be it hereby enacted by the Dalton Model Congress...

Preamble:

Section 1:
    Subsection A:
        Sub-Subsection I:

Section 2:
    Subsection A:
    Subsection B:

Section 3:
    Subsection A:
    Subsection B:
    Subsection C:

Section 4: Funding for this bill shall ____.

Section 5: This bill will be enacted ____.`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(billTemplate);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([billTemplate], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'model-congress-bill-template.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Model Congress Bill Template
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowTemplate(!showTemplate)}
            className="flex items-center text-sm text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
          >
            {showTemplate ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {showTemplate ? 'Hide' : 'Show'} Template
          </button>
        </div>
      </div>

      {showTemplate && (
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">
              {billTemplate}
            </pre>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              💡 Use this template as a reference when creating your own bill
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={copyToClipboard}
                className="flex items-center px-3 py-1 text-sm bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded hover:bg-primary-200 dark:hover:bg-primary-800"
              >
                <Copy className="w-4 h-4 mr-1" />
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={downloadTemplate}
                className="flex items-center px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </button>
              {onUseTemplate && (
                <button
                  onClick={() => onUseTemplate(billTemplate)}
                  className="flex items-center px-3 py-1 text-sm bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-800"
                >
                  Use Template
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!showTemplate && (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Click "Show Template" to view the Model Congress bill format. This template includes the standard structure with preamble, sections, subsections, and funding/enactment clauses.
        </div>
      )}
    </div>
  );
};

export default BillTemplate;
